import { CloseOutlined, FilePdfOutlined, FileTextOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import type { CSSProperties, Dispatch, SetStateAction } from "react";
import { deleteAttachment, uploadAttachment } from "../../api/attachmentApi";
import type { BulkAttachmentItem, BulkDailyEntryRow } from "./bulkTypes";

export function BulkAttachmentCell({
  row,
  type,
  setRows
}: {
  row: BulkDailyEntryRow;
  type: "bill" | "voucher";
  setRows: Dispatch<SetStateAction<BulkDailyEntryRow[]>>;
}) {
  const idsKey = type === "bill" ? "billAttachmentIds" : "voucherAttachmentIds";
  const listKey = type === "bill" ? "billAttachments" : "voucherAttachments";
  const items = row[listKey] ?? [];

  const syncAttachment = (clientId: string, patch: Partial<BulkAttachmentItem>) => {
    setRows((current) => current.map((item) => {
      if (item.key !== row.key) return item;
      const nextItems = (item[listKey] ?? []).map((attachment) => (
        attachment.clientId === clientId ? { ...attachment, ...patch } : attachment
      ));
      const nextIds = nextItems
        .filter((attachment) => attachment.status === "completed" && attachment.serverId)
        .map((attachment) => String(attachment.serverId));
      return { ...item, [listKey]: nextItems, [idsKey]: nextIds };
    }));
  };

  const addAttachment = (file: File) => {
    const clientId = crypto.randomUUID();
    const attachment: BulkAttachmentItem = {
      clientId,
      name: file.name,
      contentType: file.type,
      size: file.size,
      status: "uploading",
      progress: 0,
      previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
      file
    };

    setRows((current) => current.map((item) => (
      item.key === row.key ? { ...item, [listKey]: [...(item[listKey] ?? []), attachment] } : item
    )));

    void uploadAttachment({
      module: "DailyEntry",
      entityId: 0,
      tempKey: row.key,
      attachmentType: type,
      file,
      onProgress: (percent) => syncAttachment(clientId, { progress: percent })
    }).then((serverId) => {
      syncAttachment(clientId, { status: "completed", progress: 100, serverId: String(serverId), error: undefined });
    }).catch((error) => {
      syncAttachment(clientId, {
        status: "failed",
        progress: 0,
        error: error instanceof Error ? error.message : "Upload failed"
      });
    });
  };

  const removeAttachment = (attachment: BulkAttachmentItem) => {
    if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    setRows((current) => current.map((item) => {
      if (item.key !== row.key) return item;
      const nextItems = (item[listKey] ?? []).filter((x) => x.clientId !== attachment.clientId);
      const nextIds = nextItems
        .filter((x) => x.status === "completed" && x.serverId)
        .map((x) => String(x.serverId));
      return { ...item, [listKey]: nextItems, [idsKey]: nextIds };
    }));
    if (attachment.status === "completed" && attachment.serverId) {
      void deleteAttachment(attachment.serverId).catch(() => undefined);
    }
  };

  const retryAttachment = (attachment: BulkAttachmentItem) => {
    if (!attachment.file) return;
    syncAttachment(attachment.clientId, { status: "uploading", progress: 0, error: undefined });
    void uploadAttachment({
      module: "DailyEntry",
      entityId: 0,
      tempKey: row.key,
      attachmentType: type,
      file: attachment.file,
      onProgress: (percent) => syncAttachment(attachment.clientId, { progress: percent })
    }).then((serverId) => {
      syncAttachment(attachment.clientId, { status: "completed", progress: 100, serverId: String(serverId), error: undefined });
    }).catch((error) => {
      syncAttachment(attachment.clientId, {
        status: "failed",
        progress: 0,
        error: error instanceof Error ? error.message : "Upload failed"
      });
    });
  };

  return (
    <div className="bulk-attachment-cell">
      <div className="bulk-thumb-list">
        {items.map((attachment) => (
          <div key={attachment.clientId} className={`bulk-thumb bulk-thumb-${attachment.status}`} style={{ "--progress": `${attachment.progress}%` } as CSSProperties}>
            <div className="bulk-thumb-progress" />
            <div className="bulk-thumb-preview">
              {attachment.previewUrl ? (
                <img src={attachment.previewUrl} alt={attachment.name} />
              ) : attachment.contentType === "application/pdf" ? (
                <FilePdfOutlined />
              ) : (
                <FileTextOutlined />
              )}
            </div>
            <Tooltip title={attachment.name}>
              <span className="bulk-thumb-name">{attachment.name}</span>
            </Tooltip>
            <button
              type="button"
              className="bulk-thumb-remove"
              title="Remove"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                removeAttachment(attachment);
              }}
            >
              <CloseOutlined />
            </button>
            {attachment.status === "failed" ? (
              <button
                type="button"
                className="bulk-thumb-retry"
                title="Retry upload"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  retryAttachment(attachment);
                }}
              >
                <ReloadOutlined />
              </button>
            ) : null}
          </div>
        ))}
        <label className={`bulk-upload-tile${items.length ? " has-files" : ""}`}>
          <input
            type="file"
            multiple
            className="bulk-file-input"
            onChange={(event) => {
              Array.from(event.target.files ?? []).forEach(addAttachment);
              event.target.value = "";
            }}
          />
          <PlusOutlined />
          <span>{items.length ? "Add" : "Attach"}</span>
        </label>
      </div>
    </div>
  );
}
